import { create } from 'zustand';
import { channelTypingKey, activeTypers } from '@/lib/realtime/channel-realtime';

export interface TypingUser {
  userId: string;
  userName: string;
  /** Wall-clock ms when this entry should be considered stale. */
  expiresAt: number;
}

/** One customer (end-user) typing entry, keyed by conversation. */
export interface CustomerTyping {
  /** Wall-clock ms when this entry should be considered stale. */
  expiresAt: number;
}

interface TypingState {
  /** conversationId → userId → TypingUser. Map-of-Maps so removal is O(1). */
  byConversation: Map<string, Map<string, TypingUser>>;
  /**
   * conversationId → CustomerTyping. The CUSTOMER's "escribiendo…" lane, kept
   * strictly separate from the agent→agent `byConversation` lane above: the
   * two are driven by different WS events (`inbox:customer_typing` vs
   * `inbox:typing`) and surface in different UI. At most one customer types
   * per conversation, so this is a flat Map (not a Map-of-Maps).
   */
  customerByConversation: Map<string, CustomerTyping>;
  /**
   * Tick token bumped whenever the store mutates. Components subscribe to it
   * with a selector so the React render scheduler picks up changes — Maps
   * are mutated in place for performance, but Zustand needs a referentially
   * different value to trigger re-renders.
   */
  tick: number;
  setTyping(conversationId: string, user: { userId: string; userName: string }): void;
  /**
   * Drop one user's typing entry immediately (without waiting for the TTL).
   * Used when `inbox:agent_left_viewing` arrives — an agent who closed the
   * conversation can't still be "escribiendo" in it.
   */
  clearUser(conversationId: string, userId: string): void;
  /**
   * Drive the customer typing lane. `isTyping:false` clears immediately;
   * `true` (re)arms a short TTL. The backend may never send a stop event, so
   * the entry also self-expires via the periodic `clearStale` sweep.
   */
  setCustomerTyping(conversationId: string, isTyping: boolean): void;
  /** Whether the customer is currently (non-stale) typing in this conversation. */
  isCustomerTyping(conversationId: string): boolean;
  clearStale(): void;
}

/**
 * Backend (`internal/socketio/events.go`) does not emit a "stop typing"
 * event — clients are expected to expire entries on their own. We use a
 * 4-second TTL: each `inbox:typing` event extends the user's entry for
 * that long. Senders throttle to once every 2.5s (see realtime-manager
 * `sendTyping`), so an actively-typing user keeps the indicator alive
 * with at least 1.5s of slack.
 */
const TYPING_TTL_MS = 4000;

/**
 * Customer typing TTL — a touch longer than the agent one (~5s per spec). A
 * person typing on their phone pauses more between bursts than the FE's 2.5s
 * agent-keystroke throttle, and the backend may coalesce/throttle the customer
 * signal, so we give it extra slack before the indicator vanishes.
 */
const CUSTOMER_TYPING_TTL_MS = 5000;

export const useTypingStore = create<TypingState>((set, get) => ({
  byConversation: new Map(),
  customerByConversation: new Map(),
  tick: 0,

  setTyping: (conversationId, user) => {
    set((state) => {
      const conv = state.byConversation.get(conversationId) ?? new Map<string, TypingUser>();
      conv.set(user.userId, {
        userId: user.userId,
        userName: user.userName,
        expiresAt: Date.now() + TYPING_TTL_MS,
      });
      state.byConversation.set(conversationId, conv);
      return { tick: state.tick + 1 };
    });
  },

  clearUser: (conversationId, userId) => {
    set((state) => {
      const conv = state.byConversation.get(conversationId);
      if (!conv?.has(userId)) return {};
      conv.delete(userId);
      if (conv.size === 0) state.byConversation.delete(conversationId);
      return { tick: state.tick + 1 };
    });
  },

  setCustomerTyping: (conversationId, isTyping) => {
    set((state) => {
      if (isTyping) {
        state.customerByConversation.set(conversationId, {
          expiresAt: Date.now() + CUSTOMER_TYPING_TTL_MS,
        });
      } else {
        state.customerByConversation.delete(conversationId);
      }
      return { tick: state.tick + 1 };
    });
  },

  isCustomerTyping: (conversationId) => {
    const entry = get().customerByConversation.get(conversationId);
    return !!entry && entry.expiresAt > Date.now();
  },

  clearStale: () => {
    const now = Date.now();
    let changed = false;
    set((state) => {
      for (const [convId, users] of state.byConversation) {
        for (const [userId, u] of users) {
          if (u.expiresAt <= now) {
            users.delete(userId);
            changed = true;
          }
        }
        if (users.size === 0) state.byConversation.delete(convId);
      }
      for (const [convId, c] of state.customerByConversation) {
        if (c.expiresAt <= now) {
          state.customerByConversation.delete(convId);
          changed = true;
        }
      }
      return changed ? { tick: state.tick + 1 } : {};
    });
  },
}));

// Periodic sweep so stale entries vanish even when no new events arrive.
// 1s cadence is plenty given the 4s TTL — the indicator disappears within
// at most 1s of the last event being older than the TTL.
if (typeof window !== 'undefined') {
  setInterval(() => useTypingStore.getState().clearStale(), 1000);
}

/**
 * Hook-friendly selector. Returns the list of users typing in a conversation,
 * sorted so the most-recent typer shows first. Subscribes to `tick` so
 * re-renders fire when the underlying Map mutates.
 *
 * Stale entries are NOT re-checked here (Date.now would make this an impure
 * hook). The store's `clearStale` sweep runs every 1s, so an entry can only
 * remain "ghost-visible" for at most that long after its TTL expires — fine
 * for a typing indicator.
 */
export function useTypingUsersFor(conversationId: string | null | undefined): TypingUser[] {
  const tick = useTypingStore((s) => s.tick);
  // Touch tick so eslint-react-hooks knows we depend on it.
  void tick;
  if (!conversationId) return [];
  const conv = useTypingStore.getState().byConversation.get(conversationId);
  if (!conv) return [];
  return Array.from(conv.values()).sort((a, b) => b.expiresAt - a.expiresAt);
}

/**
 * Hook-friendly selector for the CUSTOMER typing lane. Returns true while the
 * end-user is composing in this conversation. Subscribes to `tick` so it
 * re-renders when the store mutates; staleness is reconciled by the 1s
 * `clearStale` sweep (same trade-off as `useTypingUsersFor`).
 */
export function useCustomerTypingFor(conversationId: string | null | undefined): boolean {
  const tick = useTypingStore((s) => s.tick);
  void tick;
  if (!conversationId) return false;
  return useTypingStore.getState().isCustomerTyping(conversationId);
}

/**
 * Hook-friendly selector for TEAM-CHAT channel typers. The channel lane
 * reuses the generic agent-typing map under a `'channel:'+id` key (see
 * `channelTypingKey`), so it inherits the same 4s TTL + 1s `clearStale`
 * sweep for free.
 *
 * `now = 0` disables eager expiry filtering during render — `Date.now()` in
 * a hook violates `react-hooks/purity`, and staleness is reconciled by the
 * store's 1s sweep anyway (same trade-off as `useTypingUsersFor`): a ghost
 * entry survives at most ~1s past its TTL before the sweep deletes it and
 * bumps `tick`.
 */
export function useChannelTypers(channelId: string | null | undefined): TypingUser[] {
  const tick = useTypingStore((s) => s.tick);
  void tick;
  if (!channelId) return [];
  const typers = useTypingStore.getState().byConversation.get(channelTypingKey(channelId));
  if (!typers) return [];
  return activeTypers(Array.from(typers.values()), 0);
}
